import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, AlertTriangle, CheckCircle2, Upload, RotateCcw } from 'lucide-react';

type PurgePreviewItem = {
  id: string;
  orderNumber: string;
  productName: string;
  customerName: string | null;
  status: string;
  createdAt: string;
};

type PurgeSummaryItem = {
  orderNumber: string;
  productName: string;
  customerName: string | null;
  status: string;
};

type PageState = 'input' | 'preview' | 'result';

const STATUS_LABELS: Record<string, string> = {
  CREATED: 'Creada',
  AUTHORIZED: 'Autorizada',
  COMPLETED: 'Completada',
  CLOSED: 'Cerrada',
};

function parseOrderNumbers(raw: string): string[] {
  return raw
    .split(/[\n,;\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export default function OrdersPurge() {
  const [pageState, setPageState] = useState<PageState>('input');
  const [textarea, setTextarea] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [found, setFound] = useState<PurgePreviewItem[]>([]);
  const [notFound, setNotFound] = useState<string[]>([]);

  const [deletedCount, setDeletedCount] = useState(0);
  const [summary, setSummary] = useState<PurgeSummaryItem[]>([]);
  const [showSummary, setShowSummary] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const token = () => localStorage.getItem('token');

  const handleCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const numbers = parseOrderNumbers(text);
      setTextarea((prev) => {
        const existing = parseOrderNumbers(prev);
        const merged = Array.from(new Set([...existing, ...numbers]));
        return merged.join('\n');
      });
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleVerify = async () => {
    setError(null);
    const orderNumbers = parseOrderNumbers(textarea);
    if (orderNumbers.length === 0) {
      setError('Ingresa al menos un número de orden.');
      return;
    }
    if (orderNumbers.length > 500) {
      setError('Máximo 500 órdenes por operación.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/orders/purge/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ orderNumbers }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Error al verificar órdenes');
      }
      const data = await res.json();
      setFound(data.found);
      setNotFound(data.notFound);
      setPageState('preview');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setError(null);
    const orderNumbers = found.map((o) => o.orderNumber);
    setLoading(true);
    try {
      const res = await fetch('/api/orders/purge/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ orderNumbers }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Error al ejecutar purga');
      }
      const data = await res.json();
      setDeletedCount(data.deletedCount);
      setSummary(data.summary);
      setPageState('result');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPageState('input');
    setTextarea('');
    setFound([]);
    setNotFound([]);
    setDeletedCount(0);
    setSummary([]);
    setShowSummary(false);
    setError(null);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Trash2 className="h-6 w-6 text-red-600" />
        <h1 className="text-2xl font-bold text-gray-900">Depuración de Órdenes</h1>
      </div>

      <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          Esta operación es <strong>irreversible</strong>. Las órdenes seleccionadas y todos sus
          datos relacionados (materiales, eventos, rutas, cierres, checklists, etc.) se eliminarán
          permanentemente del sistema.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 mb-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Estado: input */}
      {pageState === 'input' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Números de orden — uno por línea o separados por coma, punto y coma o espacio
            </label>
            <textarea
              className="w-full h-48 p-3 border border-gray-300 rounded-md font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-red-400"
              placeholder={"OP-001\nOP-002\nOP-003"}
              value={textarea}
              onChange={(e) => setTextarea(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <input
              type="file"
              accept=".csv,.txt"
              ref={fileInputRef}
              className="hidden"
              onChange={handleCSV}
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Cargar CSV / TXT
            </Button>
            <Button
              onClick={handleVerify}
              disabled={loading || textarea.trim().length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? 'Verificando…' : 'Verificar órdenes'}
            </Button>
          </div>
        </div>
      )}

      {/* Estado: preview */}
      {pageState === 'preview' && (
        <div className="space-y-4">
          {notFound.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-700 mb-2">
                No encontradas ({notFound.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {notFound.map((n) => (
                  <span
                    key={n}
                    className="text-xs bg-red-100 text-red-700 border border-red-300 px-2 py-0.5 rounded-full"
                  >
                    {n}
                  </span>
                ))}
              </div>
            </div>
          )}

          {found.length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <p className="text-sm font-semibold text-gray-700">
                  Órdenes encontradas — se eliminarán ({found.length})
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Nº Orden</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Producto</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Cliente</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Estatus</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Creada</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {found.map((o) => (
                      <tr key={o.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono font-medium text-gray-900">
                          {o.orderNumber}
                        </td>
                        <td className="px-4 py-2 text-gray-700">{o.productName}</td>
                        <td className="px-4 py-2 text-gray-500">{o.customerName ?? '—'}</td>
                        <td className="px-4 py-2">
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                            {STATUS_LABELS[o.status] ?? o.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {new Date(o.createdAt).toLocaleDateString('es-MX')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500">
              Ninguna de las órdenes ingresadas existe en el sistema.
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={reset} className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />
              Nueva búsqueda
            </Button>
            <Button
              onClick={handleExecute}
              disabled={loading || found.length === 0}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {loading
                ? 'Eliminando…'
                : `Confirmar eliminación (${found.length} orden${found.length !== 1 ? 'es' : ''})`}
            </Button>
          </div>
        </div>
      )}

      {/* Estado: result */}
      {pageState === 'result' && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-300 rounded-lg p-5 flex items-center gap-4">
            <CheckCircle2 className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-lg font-semibold text-green-800">
                {deletedCount} orden{deletedCount !== 1 ? 'es' : ''} eliminada
                {deletedCount !== 1 ? 's' : ''} correctamente
              </p>
              <p className="text-sm text-green-600">
                El evento ha sido registrado en la bitácora de auditoría.
              </p>
            </div>
          </div>

          {summary.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <button
                className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 text-left text-sm font-semibold text-gray-700 hover:bg-gray-100 flex justify-between items-center"
                onClick={() => setShowSummary((v) => !v)}
              >
                <span>Resumen de lo eliminado ({summary.length})</span>
                <span>{showSummary ? '▲' : '▼'}</span>
              </button>
              {showSummary && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Nº Orden</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Producto</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Cliente</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Estatus</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {summary.map((o) => (
                        <tr key={o.orderNumber} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-mono font-medium text-gray-900">
                            {o.orderNumber}
                          </td>
                          <td className="px-4 py-2 text-gray-700">{o.productName}</td>
                          <td className="px-4 py-2 text-gray-500">{o.customerName ?? '—'}</td>
                          <td className="px-4 py-2">
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                              {STATUS_LABELS[o.status] ?? o.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={reset} variant="outline" className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />
              Depurar más órdenes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
